import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  Send,
  UserPlus,
  AlertCircle,
  Clock,
  CalendarCheck,
  ClipboardCheck,
  Users,
} from 'lucide-react';

import { useRole } from '../contexts/RoleContext.jsx';
import { useApiData } from '../hooks/useApiData.js';
import { useToast } from '../contexts/ToastContext.jsx';
import { ErrorBanner, InlineSpinner } from '../components/common/PageState.jsx';
import { listLeads, nudgeAgent } from '../services/api.js';
import {
  leads as seedLeadList,
  leadById as seedLeadById,
  agents,
  agentById,
  showings,
  propertyById,
  NOW_ISO,
} from '../data/seedData.js';

const NOW = new Date(NOW_ISO);
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

// ── helpers ────────────────────────────────────────────────────────────────

function scorePillClass(score) {
  if (score >= 80) return 'bg-score-green/10 text-score-green ring-1 ring-score-green/40';
  if (score >= 50) return 'bg-score-yellow/10 text-score-yellow ring-1 ring-score-yellow/40';
  return 'bg-score-red/10 text-score-red ring-1 ring-score-red/40';
}

const TEMP_BADGE = {
  hot: { label: 'Hot', cls: 'bg-[#FEE2E2] text-[#DC2626]' },
  warm: { label: 'Warm', cls: 'bg-[#FEF3C7] text-[#D97706]' },
  nurture: { label: 'Nurture', cls: 'bg-[#DBEAFE] text-[#2563EB]' },
  cold: { label: 'Cold', cls: 'bg-[#F3F4F6] text-[#6B7280]' },
  dead: { label: 'Dead', cls: 'bg-[#F3F4F6] text-[#6B7280]' },
  red_flag: { label: 'Red Flag', cls: 'bg-[#FEE2E2] text-[#DC2626]' },
};

function relativeAgo(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  const diffH = (NOW - then) / HOUR_MS;
  if (diffH < 1) return `${Math.round(diffH * 60)}m ago`;
  if (diffH < 24) return `${Math.round(diffH)}h ago`;
  return `${Math.round(diffH / 24)}d ago`;
}

function relativeAhead(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  const diffH = (then - NOW) / HOUR_MS;
  if (diffH < 1) return `in ${Math.max(0, Math.round(diffH * 60))}m`;
  if (diffH < 24) return `in ${Math.round(diffH)}h`;
  return `in ${Math.round(diffH / 24)}d`;
}

// ── card shell ─────────────────────────────────────────────────────────────

function SectionCard({ icon: Icon, title, count, accent, children }) {
  return (
    <section className="card p-6 md:px-7 md:py-6 space-y-4">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`h-9 w-9 rounded-btn flex items-center justify-center ${accent}`}>
            <Icon size={18} />
          </div>
          <div>
            <h2 className="section-header mb-0">{title}</h2>
          </div>
        </div>
        <span className="badge bg-bg-subtle border border-border-card text-text-secondary tabular-nums">
          {count}
        </span>
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function EmptyState({ children }) {
  return (
    <div className="text-sm text-text-secondary italic px-1">{children}</div>
  );
}

// ── row components ─────────────────────────────────────────────────────────

function LeadIdentity({ lead }) {
  const temp = TEMP_BADGE[lead.temperature] || TEMP_BADGE.cold;
  return (
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      <Link
        to={`/leads/${lead.id}`}
        className="font-semibold text-text-primary hover:text-accent truncate"
      >
        {lead.name}
      </Link>
      <span className={`badge tabular-nums ${scorePillClass(lead.score)}`}>
        {lead.score}
      </span>
      <span className={`badge ${temp.cls}`}>{temp.label}</span>
    </div>
  );
}

function UnassignedRow({ lead, onClaim, onAssign, assigning }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-btn border border-border-card p-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0 flex-1">
        <LeadIdentity lead={lead} />
        <div className="mt-1 text-xs text-text-secondary">
          {lead.source} · first contact {relativeAgo(lead.firstContactAt)}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:shrink-0">
        <button
          type="button"
          onClick={() => onClaim(lead.id)}
          style={{ borderColor: '#EA580C', color: '#EA580C', borderWidth: '1.5px' }}
          className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                     hover:bg-[rgba(234,88,12,0.10)]
                     px-4 py-1.5 text-[13px] font-medium transition-colors"
        >
          <ClipboardCheck size={13} /> Claim
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            style={{ borderColor: '#9CA3AF', color: '#9CA3AF', borderWidth: '1.5px' }}
            className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                       hover:bg-[rgba(107,114,128,0.10)] hover:text-[#333333]
                       px-4 py-1.5 text-[13px] font-medium transition-colors"
          >
            <UserPlus size={13} /> Assign
          </button>
          {open && (
            <div
              className="absolute right-0 mt-2 w-56 card p-1 z-10 shadow-lg"
              onMouseLeave={() => setOpen(false)}
            >
              {assigning.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    onAssign(lead.id, a.id);
                    setOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 text-sm rounded-btn
                             hover:bg-accent/10 text-text-primary"
                >
                  <span className="font-medium">{a.name}</span>
                  <span className="block text-xs text-text-secondary">{a.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StaleContactRow({ lead, onNudge, nudging }) {
  const agent = lead.assignedAgentId ? agentById[lead.assignedAgentId] : null;
  const hours = Math.round((NOW - new Date(lead.lastContactAt)) / HOUR_MS);
  return (
    <div className="rounded-btn border border-border-card p-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0 flex-1">
        <LeadIdentity lead={lead} />
        <div className="mt-1 text-xs text-text-secondary">
          Last contact {hours}h ago ·{' '}
          {agent ? (
            <span className="text-text-primary">{agent.name}</span>
          ) : (
            <span className="italic">Unassigned</span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:shrink-0">
        <a
          href={lead.phone ? `tel:${lead.phone.replace(/[^\d+]/g, '')}` : '#'}
          style={{ borderColor: '#16A34A', color: '#16A34A', borderWidth: '1.5px' }}
          className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                     hover:bg-[rgba(22,163,74,0.10)]
                     px-4 py-1.5 text-[13px] font-medium transition-colors"
        >
          <Phone size={13} /> Call
        </a>
        <button
          type="button"
          onClick={() => onNudge(lead)}
          disabled={nudging}
          style={{ borderColor: '#6B7280', color: '#6B7280', borderWidth: '1.5px' }}
          className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                     hover:bg-[rgba(107,114,128,0.10)]
                     px-4 py-1.5 text-[13px] font-medium transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send size={13} /> {nudging ? 'Nudging…' : 'Nudge Agent'}
        </button>
      </div>
    </div>
  );
}

function ShowingRow({ showing, leadsById }) {
  const lead = leadsById[showing.leadId] || seedLeadById[showing.leadId];
  if (!lead) return null;
  const property = showing.propertyId ? propertyById[showing.propertyId] : null;
  const agent = showing.agentId ? agentById[showing.agentId] : null;
  return (
    <div className="rounded-btn border border-border-card p-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0 flex-1">
        <LeadIdentity lead={lead} />
        <div className="mt-1 text-xs text-text-secondary truncate">
          {property ? `${property.address}` : 'Property TBD'} · {relativeAhead(showing.scheduledAt)}
          {agent && <> · with {agent.name.split(' ')[0]}</>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:shrink-0">
        <button
          type="button"
          style={{ borderColor: '#6B7280', color: '#6B7280', borderWidth: '1.5px' }}
          className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                     hover:bg-[rgba(107,114,128,0.10)]
                     px-4 py-1.5 text-[13px] font-medium transition-colors"
        >
          <Send size={13} /> Send Reminder
        </button>
        <a
          href={lead.phone ? `tel:${lead.phone.replace(/[^\d+]/g, '')}` : '#'}
          style={{ borderColor: '#16A34A', color: '#16A34A', borderWidth: '1.5px' }}
          className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                     hover:bg-[rgba(22,163,74,0.10)]
                     px-4 py-1.5 text-[13px] font-medium transition-colors"
        >
          <Phone size={13} /> Call
        </a>
      </div>
    </div>
  );
}

function FollowupRow({ lead }) {
  const agent = lead.assignedAgentId ? agentById[lead.assignedAgentId] : null;
  const days = Math.floor((NOW - new Date(lead.lastContactAt)) / DAY_MS);
  return (
    <div className="rounded-btn border border-border-card p-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0 flex-1">
        <LeadIdentity lead={lead} />
        <div className="mt-1 text-xs text-text-secondary">
          Qualified {days}d ago · no follow-up ·{' '}
          {agent ? (
            <span className="text-text-primary">{agent.name}</span>
          ) : (
            <span className="italic">Unassigned</span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:shrink-0">
        <a
          href={lead.phone ? `tel:${lead.phone.replace(/[^\d+]/g, '')}` : '#'}
          style={{ borderColor: '#16A34A', color: '#16A34A', borderWidth: '1.5px' }}
          className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                     hover:bg-[rgba(22,163,74,0.10)]
                     px-4 py-1.5 text-[13px] font-medium transition-colors"
        >
          <Phone size={13} /> Call Now
        </a>
        <button
          type="button"
          style={{ borderColor: '#6B7280', color: '#6B7280', borderWidth: '1.5px' }}
          className="inline-flex items-center gap-1.5 rounded-[20px] bg-transparent border
                     hover:bg-[rgba(107,114,128,0.10)]
                     px-4 py-1.5 text-[13px] font-medium transition-colors"
        >
          <UserPlus size={13} /> Reassign
        </button>
      </div>
    </div>
  );
}

// ── page ───────────────────────────────────────────────────────────────────

export default function Followups() {
  const { currentUser } = useRole();
  const { showToast } = useToast();

  // Pull leads from the API when available; fall back to seed silently.
  const leadsReq = useApiData(() => listLeads({ limit: 500 }), []);
  const leads = leadsReq.data?.leads?.length ? leadsReq.data.leads : seedLeadList;

  // Local overrides so Claim/Assign feel responsive without a backend.
  const [assignments, setAssignments] = useState({});
  const [nudgingId, setNudgingId] = useState(null);

  const leadsById = useMemo(
    () => Object.fromEntries(leads.map((l) => [l.id, l])),
    [leads],
  );

  const handleNudge = async (lead) => {
    if (!lead) return;
    if (!lead.assignedAgentId) {
      showToast('No agent assigned to nudge', { type: 'warning' });
      return;
    }
    setNudgingId(lead.id);
    try {
      await nudgeAgent({ leadId: lead.id, agentId: lead.assignedAgentId });
      showToast('Agent nudged', { type: 'success' });
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Could not nudge agent', {
        type: 'error',
      });
    } finally {
      setNudgingId(null);
    }
  };

  const resolvedLeads = useMemo(
    () =>
      leads.map((lead) =>
        Object.prototype.hasOwnProperty.call(assignments, lead.id)
          ? { ...lead, assignedAgentId: assignments[lead.id] }
          : lead,
      ),
    [assignments, leads],
  );

  const claim = (leadId) => {
    if (!currentUser) return;
    setAssignments((prev) => ({ ...prev, [leadId]: currentUser.id }));
  };

  const assign = (leadId, agentId) => {
    setAssignments((prev) => ({ ...prev, [leadId]: agentId }));
  };

  // Section datasets
  const unassigned = resolvedLeads.filter(
    (l) => !l.assignedAgentId && !['closed_lost', 'dead', 'disqualified'].includes(l.status),
  );

  const staleContact = resolvedLeads.filter((l) => {
    if (['closed_won', 'closed_lost', 'dead', 'disqualified'].includes(l.status)) return false;
    const hours = (NOW - new Date(l.lastContactAt)) / HOUR_MS;
    return hours >= 48;
  });

  const needsConfirmation = showings
    .filter((s) => {
      if (s.status !== 'needs_confirmation' && s.status !== 'scheduled') return false;
      const hoursOut = (new Date(s.scheduledAt) - NOW) / HOUR_MS;
      return hoursOut > 0 && hoursOut <= 48 && s.status === 'needs_confirmation';
    })
    .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));

  const followupsDue = resolvedLeads
    .filter((l) => {
      if (l.status !== 'qualified') return false;
      const hours = (NOW - new Date(l.lastContactAt)) / HOUR_MS;
      return hours >= 48;
    })
    .sort((a, b) => b.score - a.score);

  // Agents available to assign TO (leaders + agents, not ISAs — they route, not own).
  const assignableAgents = agents.filter((a) => a.role === 'team_leader' || a.role === 'agent');

  return (
    <div className="space-y-6">
      <header>
        <div className="flex items-center gap-3">
          <h1 className="page-title mb-0">Follow-ups</h1>
          {leadsReq.loading && <InlineSpinner />}
        </div>
        <p className="page-subtitle mb-0 mt-2">
          Everything waiting on an ISA: unassigned leads, stale contacts, showings to confirm,
          and qualified leads that need a push.
        </p>
      </header>

      {leadsReq.error && (
        <ErrorBanner
          message="Couldn’t load leads from the server."
          onRetry={leadsReq.refetch}
        />
      )}

      <div className="grid grid-cols-1 lg:[grid-template-columns:repeat(2,minmax(0,1fr))] gap-6 items-start">
        <SectionCard
          icon={Users}
          title="Unassigned Leads"
          count={unassigned.length}
          accent="bg-[#FEE2E2] text-[#DC2626]"
        >
          {unassigned.length === 0 ? (
            <EmptyState>All leads have an agent. Nice.</EmptyState>
          ) : (
            unassigned.map((lead) => (
              <UnassignedRow
                key={lead.id}
                lead={lead}
                onClaim={claim}
                onAssign={assign}
                assigning={assignableAgents}
              />
            ))
          )}
        </SectionCard>

        <SectionCard
          icon={Clock}
          title="No Contact 48h+"
          count={staleContact.length}
          accent="bg-[#FEF3C7] text-[#D97706]"
        >
          {staleContact.length === 0 ? (
            <EmptyState>Every live lead has been touched in the last two days.</EmptyState>
          ) : (
            staleContact.slice(0, 8).map((lead) => (
              <StaleContactRow
                key={lead.id}
                lead={lead}
                onNudge={handleNudge}
                nudging={nudgingId === lead.id}
              />
            ))
          )}
        </SectionCard>

        <SectionCard
          icon={CalendarCheck}
          title="Showings Needing Confirmation"
          count={needsConfirmation.length}
          accent="bg-[#DBEAFE] text-[#2563EB]"
        >
          {needsConfirmation.length === 0 ? (
            <EmptyState>All upcoming showings are confirmed.</EmptyState>
          ) : (
            needsConfirmation.map((s) => (
              <ShowingRow key={s.id} showing={s} leadsById={leadsById} />
            ))
          )}
        </SectionCard>

        <SectionCard
          icon={AlertCircle}
          title="Follow-ups Due"
          count={followupsDue.length}
          accent="bg-[#FFEDD5] text-[#C2410C]"
        >
          {followupsDue.length === 0 ? (
            <EmptyState>No stale qualified leads.</EmptyState>
          ) : (
            followupsDue.slice(0, 8).map((lead) => <FollowupRow key={lead.id} lead={lead} />)
          )}
        </SectionCard>
      </div>
    </div>
  );
}
